/*
 * Factory Tour Waitlist — pushes signups straight to Klaviyo from the browser.
 *
 * Uses Klaviyo's public "Create Client Subscription" endpoint, which only needs the
 * account's public API key (company_id), so nothing secret ships in the theme. One call:
 *   - creates the profile if it's new, or updates it if the email already exists
 *   - sets first_name and the custom profile property `waitlist_tickets_requested`
 *   - adds the profile to the configured list (with email marketing consent if enabled)
 * Klaviyo answers 202 Accepted with no body on success.
 */
if (!customElements.get('factory-tour-waitlist')) {
  customElements.define(
    'factory-tour-waitlist',
    class FactoryTourWaitlist extends HTMLElement {
      static KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
      static KLAVIYO_REVISION = '2025-07-15';
      static EVENT_NAME = 'Joined Factory Tour Waitlist';

      constructor() {
        super();
        this.form = this.querySelector('[data-ftw-form]');
        this.submitButton = this.querySelector('[data-ftw-submit]');
        this.errorEl = this.querySelector('[data-ftw-error]');
        this.errorText = this.querySelector('[data-ftw-error-text]');
        this.successEl = this.querySelector('[data-ftw-success]');
        this.successSummary = this.querySelector('[data-ftw-success-summary]');

        this.companyId = (this.dataset.companyId || '').trim();
        this.listId = (this.dataset.listId || '').trim();
        this.source = this.dataset.source || 'Factory Tour Waitlist';
        this.subscribeToMarketing = this.dataset.subscribe !== 'false';

        if (!this.form) return;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.form.querySelectorAll('input').forEach((input) => {
          input.addEventListener('input', () => this.clearError());
        });
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.classList.contains('loading')) return;

        const data = this.readForm();
        const problem = this.validate(data);
        if (problem) {
          this.showError(problem.message, problem.field);
          return;
        }

        if (!this.companyId || !this.listId) {
          console.error('[factory-tour-waitlist] Klaviyo list ID / public API key not configured on the section.');
          this.showError('This form isn’t quite ready yet — please try again shortly.');
          return;
        }

        this.setLoading(true);
        this.subscribe(data)
          .then(() => {
            this.identifyOnsite(data);
            this.showSuccess(data);
          })
          .catch((error) => {
            console.error('[factory-tour-waitlist] Klaviyo subscribe failed', error);
            this.showError(error.userMessage || 'Something went wrong and we couldn’t save your spot. Please try again.');
          })
          .finally(() => this.setLoading(false));
      }

      readForm() {
        const formData = new FormData(this.form);
        return {
          firstName: String(formData.get('first_name') || '').trim(),
          email: String(formData.get('email') || '').trim().toLowerCase(),
          tickets: parseInt(formData.get('tickets'), 10),
        };
      }

      validate({ firstName, email, tickets }) {
        const ticketsInput = this.form.elements.tickets;
        const min = parseInt(ticketsInput.min, 10) || 1;
        const max = parseInt(ticketsInput.max, 10) || 99;

        if (!firstName) {
          return { field: 'first_name', message: 'Please tell us your first name.' };
        }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          return { field: 'email', message: 'Please enter a valid email address.' };
        }
        if (!Number.isInteger(tickets) || tickets < min || tickets > max) {
          return { field: 'tickets', message: `Please choose between ${min} and ${max} tickets.` };
        }
        return null;
      }

      buildPayload({ firstName, email, tickets }) {
        const profileAttributes = {
          email,
          first_name: firstName,
          properties: {
            waitlist_tickets_requested: tickets,
          },
        };

        if (this.subscribeToMarketing) {
          profileAttributes.subscriptions = {
            email: { marketing: { consent: 'SUBSCRIBED' } },
          };
        }

        return {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.source,
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes,
                },
              },
            },
            relationships: {
              list: {
                data: { type: 'list', id: this.listId },
              },
            },
          },
        };
      }

      async subscribe(data) {
        const url = `${FactoryTourWaitlist.KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: FactoryTourWaitlist.KLAVIYO_REVISION,
          },
          body: JSON.stringify(this.buildPayload(data)),
        });

        if (response.ok) return;

        // Klaviyo returns a JSON:API error document; surface its detail if there is one.
        let detail = '';
        try {
          const body = await response.json();
          detail = body?.errors?.[0]?.detail || '';
        } catch (e) {
          /* no JSON body */
        }
        const error = new Error(`Klaviyo responded ${response.status}: ${detail}`);
        error.userMessage = /email/i.test(detail)
          ? 'That email address doesn’t look right — please check it and try again.'
          : undefined;
        throw error;
      }

      // Tie the browser session to this profile so on-site tracking (viewed product, etc.)
      // attributes to them, and record an event that flows can trigger off. Best-effort only.
      identifyOnsite({ firstName, email, tickets }) {
        try {
          window.klaviyo = window.klaviyo || [];
          window.klaviyo.push(['identify', { email, first_name: firstName }]);
          window.klaviyo.push([
            'track',
            FactoryTourWaitlist.EVENT_NAME,
            { tickets_requested: tickets, source: this.source },
          ]);
        } catch (e) {
          /* onsite tracking unavailable */
        }

        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event: 'factory_tour_waitlist_signup', tickets_requested: tickets });
        }
      }

      setLoading(isLoading) {
        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.toggleAttribute('disabled', isLoading);
        this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.submitButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !isLoading);
      }

      showError(message, fieldName) {
        this.errorText.textContent = message;
        this.errorEl.classList.remove('hidden');
        if (fieldName && this.form.elements[fieldName]) {
          const field = this.form.elements[fieldName];
          field.setAttribute('aria-invalid', 'true');
          field.focus();
        }
      }

      clearError() {
        this.errorEl.classList.add('hidden');
        this.form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
      }

      showSuccess({ firstName, tickets }) {
        const noun = tickets === 1 ? 'ticket' : 'tickets';
        this.successSummary.textContent = `${firstName}, we’ve noted you’d like ${tickets} ${noun}.`;
        this.form.classList.add('hidden');
        this.successEl.classList.remove('hidden');
        this.successEl.focus({ preventScroll: true });
      }
    }
  );
}
